<template>
  <div class="main-content">
    <!-- 第一级：标签栏（阶段/范围） -->
    <div class="stage-tabs">
      <div
        v-for="stageKey in stageKeys"
        :key="stageKey"
        class="stage-tab"
        :class="{ 'stage-tab-active': currentStageKey === stageKey }"
        @click="switchStage(stageKey)"
      >
        {{ dataSummaryConfig[stageKey]?.label }}
      </div>
    </div>

    <!-- 数据汇总内容区域 -->
    <div class="summary-content">
      <!-- 第二级：分类列表 -->
      <div
        v-for="(category, categoryKey) in currentStageCategoriesWithSources"
        :key="categoryKey"
        class="summary-category"
      >
        <!-- 分类标题（可折叠） -->
        <div
          class="category-header"
          :class="{ 'category-expanded': isCategoryExpanded(currentStageKey, categoryKey) }"
          @click="toggleCategory(currentStageKey, categoryKey)"
        >
          <div class="category-arrow">
            <img
              :src="
                isCategoryExpanded(currentStageKey, categoryKey)
                  ? require('@/assets/images/chatpreview/arrow-down.svg')
                  : require('@/assets/images/chatpreview/arrow-right.svg')
              "
              alt="arrow"
            />
          </div>
          <div class="category-label">{{ category.label }}</div>
        </div>

        <!-- 第三级：分类下的排放源列表 -->
        <div
          class="category-items"
          v-show="isCategoryExpanded(currentStageKey, categoryKey)"
        >
          <div
            v-for="(emissionSource, emissionSourceIndex) in getEmissionSources(category)"
            :key="emissionSourceIndex"
            class="emission-source-group"
          >
            <!-- 排放源名称（可折叠） -->
            <div
              class="emission-source-header"
              :class="{ 'emission-source-expanded': isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex) }"
              @click="toggleEmissionSource(currentStageKey, categoryKey, emissionSourceIndex)"
            >
              <div class="emission-source-arrow">
                <img
                  :src="
                    isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex)
                      ? require('@/assets/images/chatpreview/arrow-down.svg')
                      : require('@/assets/images/chatpreview/arrow-right_deep.svg')
                  "
                  alt="arrow"
                />
              </div>
              <div class="emission-source-label">{{ emissionSource.name }}</div>
            </div>

            <!-- 活动数据列表 -->
            <div
              v-if="accessType === 'tpc'"
              class="activity-data-list"
              v-show="isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex)"
            >
              <div
                v-for="(activityData, activityDataIndex) in getActivityData(emissionSource)"
                :key="activityDataIndex"
                class="activity-data-item"
              >
                <el-tooltip
                  :content="activityData.type"
                  placement="bottom"
                  effect="light"
                  :open-delay="300"
                  popper-class="chat-preview-tooltip"
                >
                  <div class="activity-type">{{ activityData.type }}</div>
                </el-tooltip>
                <el-tooltip
                  :content="activityData.value"
                  placement="bottom"
                  effect="light"
                  :open-delay="300"
                  popper-class="chat-preview-tooltip"
                >
                  <div class="activity-value">
                    {{ activityData.value }}
                  </div>
                </el-tooltip>
                <el-tooltip
                  :content="activityData.year"
                  placement="bottom"
                  effect="light"
                  :open-delay="300"
                  popper-class="chat-preview-tooltip"
                >
                  <div class="activity-year">{{ activityData.year }}</div>
                </el-tooltip>
                <el-tooltip
                  :content="activityData.location"
                  placement="bottom"
                  effect="light"
                  :open-delay="300"
                  popper-class="chat-preview-tooltip"
                >
                  <div class="activity-location">{{ activityData.location }}</div>
                </el-tooltip>
              </div>
            </div>
            <div
              v-if="accessType === 'tzj'"
              class="activity-data-list"
              v-show="isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex)"
            >
              <div
                v-for="(activityData, activityDataIndex) in getActivityData(emissionSource)"
                :key="activityDataIndex"
                class="activity-data-item"
              >
                <div class="activity-type">{{ activityData.type }}</div>
                <div class="activity-value">
                  {{ activityData.value }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 在线填报按钮 -->
    <div v-if="dataSummaryConfig.link?.value" class="online-fill-button">
      <img
        src="@/assets/images/chatpreview/data_summary_online.svg"
        alt="在线填报"
        @click="handleOnlineFill"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { uploadFile } from "@/api";
export default {
  name: "DataSummary",
  inject: ['getExcelViewer'],
  props: {
    previewData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      // 第一级：当前选中的阶段/范围标签页
      currentStageKey: null,
      // 第二级：已展开的分类（按阶段分组）
      expandedCategories: {},
      // 第三级：已展开的排放源（按阶段和分类分组）
      expandedEmissionSources: {},
    };
  },
  computed: {
    ...mapState(["accessType"]),

    dataSummaryConfig() {
      // 如果 previewData 存在，优先使用预览数据，否则使用配置数据
      if (this.previewData) {
        return this.previewData;
      }
      return {};
    },
    // 第一级：获取所有阶段/范围标签页的键（按顺序），只返回有数据的阶段
    stageKeys() {
      const config = this.dataSummaryConfig;
      if (!config) return [];
      
      let keys = [];
      if (this.accessType === "tpc") {
        keys = ["scope_1", "scope_2", "scope_3"];
      } else if (this.accessType === "tzj") {
        keys = [
          "raw_material_acquisition_stage",
          "production_manufacturing_stage",
          "transportation_and_storage_stage",
        ];
      }
      
      // 过滤：只返回有实际数据的阶段（有分类且分类下有排放源）
      return keys.filter((key) => {
        const stage = config[key];
        if (!stage) return false;
        
        // 检查该阶段是否有分类和排放源数据
        const categories = this.getCategoriesForStage(stage);
        for (const categoryKey in categories) {
          const category = categories[categoryKey];
          const emissionSources = this.getEmissionSources(category);
          if (emissionSources && emissionSources.length > 0) {
            return true; // 找到有数据的分类，该阶段应该显示
          }
        }
        return false; // 没有找到有数据的分类，该阶段不显示
      });
    },
    // 第二级：获取当前阶段下有排放源的分类列表（过滤掉空分类）
    currentStageCategoriesWithSources() {
      const categories = this.getCurrentStageCategories();
      const filteredCategories = {};
      for (const categoryKey in categories) {
        const category = categories[categoryKey];
        const emissionSources = this.getEmissionSources(category);
        if (emissionSources && emissionSources.length > 0) {
          filteredCategories[categoryKey] = category;
        }
      }
      return filteredCategories;
    },
  },
  watch: {
    // 监听配置变化，自动初始化默认选中和展开状态
    dataSummaryConfig: {
      immediate: true,
      deep: true,
      handler(newConfig) {
        if (!newConfig || Object.keys(newConfig).length === 0) {
          return;
        }
        this.initializeDefaultState();
      },
    },
  },
  mounted() {
    // 组件挂载时，如果配置已存在，初始化默认状态
    if (this.dataSummaryConfig && Object.keys(this.dataSummaryConfig).length > 0) {
      this.initializeDefaultState();
    }
  },
  methods: {
    // 初始化默认状态（默认选中第一个阶段标签页）
    initializeDefaultState() {
      const config = this.dataSummaryConfig;
      if (!config || Object.keys(config).length === 0) {
        return;
      }

      // 第一级：获取第一个阶段标签页的键
      const firstStageKey = this.stageKeys[0];
      if (!firstStageKey) {
        return;
      }

      // 设置默认选中的阶段标签页
      if (this.currentStageKey !== firstStageKey) {
        this.currentStageKey = firstStageKey;
      }

      // 初始化所有阶段的展开状态
      this.stageKeys.forEach((stageKey) => {
        // 第二级：初始化分类展开状态
        if (!this.expandedCategories[stageKey]) {
          this.$set(this.expandedCategories, stageKey, []);
        }

        // 第三级：初始化排放源展开状态
        if (!this.expandedEmissionSources[stageKey]) {
          this.$set(this.expandedEmissionSources, stageKey, {});
        }

        // 获取当前阶段下的分类，并初始化排放源展开状态
        const stage = config[stageKey];
        if (stage) {
          const categories = this.getCategoriesForStage(stage);
          Object.keys(categories).forEach((categoryKey) => {
            if (!this.expandedEmissionSources[stageKey][categoryKey]) {
              this.$set(this.expandedEmissionSources[stageKey], categoryKey, []);
            }
          });
        }
      });
    },
    // 第二级：获取阶段下的分类列表（适配TPC和TZJ两种结构）
    getCategoriesForStage(stage) {
      if (!stage) return {};
      const categories = {};
      for (const key in stage) {
        // 排除 label 键
        if (key === "label") {
          continue;
        }
        
        const item = stage[key];
        // 只处理对象类型
        if (typeof item !== "object" || item === null) {
          continue;
        }
        
        // TZJ: 排除 value 是字符串的情况（如 raw_material_acquisition_stage.value = ""）
        // TPC: value 是数组，不会被排除
        if ("value" in item && typeof item.value === "string") {
          continue;
        }
        
        categories[key] = item;
      }
      return categories;
    },
    // 第一级：切换阶段/范围标签页
    switchStage(stageKey) {
      this.currentStageKey = stageKey;
      // 如果当前阶段没有展开的分类，初始化展开状态
      if (!this.expandedCategories[stageKey]) {
        this.$set(this.expandedCategories, stageKey, []);
      }
      if (!this.expandedEmissionSources[stageKey]) {
        this.$set(this.expandedEmissionSources, stageKey, {});
      }
    },
    // 第二级：获取当前阶段下的分类列表
    getCurrentStageCategories() {
      if (!this.currentStageKey) return {};
      const stage = this.dataSummaryConfig[this.currentStageKey];
      if (!stage) return {};
      return this.getCategoriesForStage(stage);
    },
    // 第三级：获取分类下的排放源列表
    getEmissionSources(category) {
      if (!category || !Array.isArray(category.value)) {
        return [];
      }
      return category.value;
    },
    // 获取排放源下的活动数据列表
    getActivityData(emissionSource) {
      if (!emissionSource || !Array.isArray(emissionSource.activityData)) {
        return [];
      }
      return emissionSource.activityData;
    },
    // 第二级：判断分类是否展开
    isCategoryExpanded(stageKey, categoryKey) {
      return (
        this.expandedCategories[stageKey] &&
        this.expandedCategories[stageKey].includes(categoryKey)
      );
    },
    // 第二级：切换分类展开/收起
    toggleCategory(stageKey, categoryKey) {
      if (!this.expandedCategories[stageKey]) {
        this.$set(this.expandedCategories, stageKey, []);
      }
      const categories = this.expandedCategories[stageKey];
      const index = categories.indexOf(categoryKey);
      if (index > -1) {
        categories.splice(index, 1);
      } else {
        categories.push(categoryKey);
      }
    },
    // 第三级：判断排放源是否展开
    isEmissionSourceExpanded(stageKey, categoryKey, emissionSourceIndex) {
      return (
        this.expandedEmissionSources[stageKey] &&
        this.expandedEmissionSources[stageKey][categoryKey] &&
        this.expandedEmissionSources[stageKey][categoryKey].includes(emissionSourceIndex)
      );
    },
    // 第三级：切换排放源展开/收起
    toggleEmissionSource(stageKey, categoryKey, emissionSourceIndex) {
      if (!this.expandedEmissionSources[stageKey]) {
        this.$set(this.expandedEmissionSources, stageKey, {});
      }
      if (!this.expandedEmissionSources[stageKey][categoryKey]) {
        this.$set(this.expandedEmissionSources[stageKey], categoryKey, []);
      }
      const emissionSources = this.expandedEmissionSources[stageKey][categoryKey];
      const index = emissionSources.indexOf(emissionSourceIndex);
      if (index > -1) {
        emissionSources.splice(index, 1);
      } else {
        emissionSources.push(emissionSourceIndex);
      }
    },
    // 检查URL是否包含指定路径
    checkUrlContainsPath(url, targetPath) {
      if (!url) return false;
      try {
        // 如果是相对路径，尝试转换为完整URL
        let urlObj;
        if (url.startsWith('http://') || url.startsWith('https://')) {
          urlObj = new URL(url);
        } else {
          // 相对路径，使用当前域名
          urlObj = new URL(url, window.location.origin);
        }
        const pathname = urlObj.pathname;
        const pathSegments = pathname.split('/').filter(segment => segment.trim() !== '');
        return pathSegments.includes(targetPath);
      } catch (err) {
        // 如果URL解析失败，尝试直接检查字符串
        return url.includes(targetPath);
      }
    },
    // 从链接中提取文件ID
    extractFileIdFromUrl(url) {
      if (!url) return null;
      try {
        // 尝试解析URL
        let urlObj;
        if (url.startsWith('http://') || url.startsWith('https://')) {
          urlObj = new URL(url);
        } else {
          urlObj = new URL(url, window.location.origin);
        }
        const pathname = urlObj.pathname;
        const pathSegments = pathname.split('/').filter(segment => segment.trim() !== '');
        // 查找 ai_open_file 的位置，然后获取下一个段作为 fileId
        const aiOpenFileIndex = pathSegments.indexOf('ai_open_file');
        if (aiOpenFileIndex >= 0 && aiOpenFileIndex < pathSegments.length - 1) {
          return pathSegments[aiOpenFileIndex + 1];
        }
        // 如果没有找到，返回路径的最后一部分
        return pathSegments[pathSegments.length - 1] || null;
      } catch (err) {
        // 如果URL解析失败，尝试直接提取
        const segments = url.split('/').filter(segment => segment.trim() !== '');
        const aiOpenFileIndex = segments.indexOf('ai_open_file');
        if (aiOpenFileIndex >= 0 && aiOpenFileIndex < segments.length - 1) {
          return segments[aiOpenFileIndex + 1];
        }
        return segments[segments.length - 1] || null;
      }
    },
    // 处理在线填报按钮点击
    handleOnlineFill() {
      const config = this.dataSummaryConfig;
      if (!config || !config.link || !config.link.value) {
        this.$message.warning("数据收集表链接不存在");
        return;
      }

      const linkUrl = config.link.value.trim();
      if (!linkUrl) {
        this.$message.warning("数据收集表链接为空");
        return;
      }

      // 检查链接是否包含 ai_open_file 路径
      const hasTarget = this.checkUrlContainsPath(linkUrl, 'ai_open_file');
      
      if (hasTarget) {
        // 提取 fileId
        const fileId = this.extractFileIdFromUrl(linkUrl);
        if (fileId) {
          // 调用 ChatPage 中的 ExcelViewer 组件打开表单
          const excelViewer = this.getExcelViewer ? this.getExcelViewer() : null;
          if (excelViewer) {
            excelViewer.showModal(fileId);
          } else {
            this.$message.error("表单组件未初始化");
          }
        } else {
          this.$message.error("无法从链接中提取文件ID");
        }
      } else {
        // 如果不是 ai_open_file 链接，尝试直接提取可能的文件ID
        // 或者显示错误提示
        this.$message.warning("链接格式不正确，无法打开表单。链接应包含 'ai_open_file' 路径");
      }
    },
    // 处理 Excel 提交成功事件
    async handleExcelSubmit(file) {
      if (!file) {
        this.$message.error("文件不存在");
        return;
      }

      // 检查文件大小
      if (file.size > 15 * 1024 * 1024) {
        this.$message({
          message: this.$t ? this.$t('chatPage.maxFileSize', {
            filename: file.name,
            formats: "15MB"
          }) : `文件 ${file.name} 大小不能超过15MB，请重试`,
          type: "warning",
        });
        return;
      }

      try {
        // 获取 ChatPage 实例
        const chatPage = this.getChatPageInstance();
        if (!chatPage) {
          this.$message.error("无法访问聊天页面");
          return;
        }

        // 确保 user 信息存在
        const userId = chatPage.requestData?.user || chatPage.userInfo?.user_id || this.$store?.state?.userInfo?.user_id;
        if (!userId) {
          this.$message.error("用户信息不存在，请重新登录");
          return;
        }

        // 上传文件
        const formData = new FormData();
        formData.append("file", file);
        formData.append("user", userId);
        formData.append("type", chatPage.fileAccept || ".xls,.xlsx,.txt,.doc,.docx,.pdf,.zip,.rar,.7z,.tar");
        formData.append("access_type", this.accessType);

        // 设置消息内容
        const messageText = this.$t ? this.$t("chatPage.messageFile") : "已上传数据收集表";
        
        const response = await uploadFile(formData);
        
        if (!response || !response.data || !response.data.data) {
          throw new Error("上传响应数据格式错误");
        }
        
        // 生成文件URL
        const fileUrl = URL.createObjectURL(file);
        
        // 将文件添加到 ChatPage 的 requestData.files
        if (!chatPage.requestData) {
          chatPage.requestData = {};
        }
        if (!chatPage.requestData.files) {
          chatPage.requestData.files = [];
        }
        chatPage.requestData.files.push({
          name: response.data.data.name || file.name,
          type: "document",
          size: (file.size / 1024).toFixed(2) + " KB",
          url: fileUrl,
          upload_file_id: response.data.data.id,
          transfer_method: "local_file",
        });

        // 设置消息内容
        chatPage.newMessage = messageText;
        
        // 关闭 ExcelViewer（使用 ChatPage 中的实例）
        const excelViewer = this.getExcelViewer ? this.getExcelViewer() : null;
        if (excelViewer) {
          excelViewer.saveDraft();
        }

        // 触发 ChatPage 的发送消息
        if (typeof chatPage.sendMessage === 'function') {
          chatPage.sendMessage();
          this.$message.success("数据收集表提交成功");
        } else {
          this.$message.error("无法触发发送消息");
        }
      } catch (error) {
        console.error("提交收集表失败:", error);
        this.$message({
          message: this.$t ? this.$t('chatPage.fileUploadError', {
            filename: file.name,
          }) : `附件 ${file.name} 上传失败，请重试`,
          type: "error",
        });
      }
    },
    // 获取 ChatPage 实例
    getChatPageInstance() {
      // 通过 $parent 链向上查找 ChatPage 组件
      let parent = this.$parent;
      let depth = 0;
      const maxDepth = 10; // 防止无限循环
      
      while (parent && depth < maxDepth) {
        // 检查是否是 ChatPage 组件
        if (parent.$options && parent.$options.name === "ChatPage") {
          return parent;
        }
        // 或者检查是否有 sendMessage 方法（更灵活）
        if (typeof parent.sendMessage === 'function' && parent.requestData) {
          return parent;
        }
        parent = parent.$parent;
        depth++;
      }
      
      // 如果找不到，尝试通过 $root 查找
      if (this.$root) {
        const findInChildren = (component) => {
          if (!component) return null;
          if (component.$options && component.$options.name === "ChatPage") {
            return component;
          }
          if (typeof component.sendMessage === 'function' && component.requestData) {
            return component;
          }
          if (component.$children) {
            for (let child of component.$children) {
              const found = findInChildren(child);
              if (found) return found;
            }
          }
          return null;
        };
        
        const found = findInChildren(this.$root);
        if (found) return found;
      }
      
      return null;
    },
  },
};
</script>

<style scoped lang="scss">
.main-content {
  width: 100%;
  min-height: 269px;
  max-height: 473px;
  padding: 12px 16px;
  position: relative;
  display: flex;
  flex-direction: column;
}

// 第一级：阶段/范围标签页样式
.stage-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
}

.stage-tab {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(5, 32, 49, 1);
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
}

.stage-tab-active {
  background-color: rgba(219, 232, 245, 1);
}

// 数据汇总内容区域
.summary-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    display: none;
  }
}

.summary-category {
  width: 100%;
}

// 第二级：分类标题样式
.category-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding-left: 10px;

  &:hover {
    background: rgba(244, 248, 252, 1);
  }
}

.category-header.category-expanded {
  background: rgba(219, 232, 245, 1);
  border-radius: 8px;
}

.category-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.category-label {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(0, 43, 37, 1);

  .category-header:not(.category-expanded) & {
    color: rgba(170, 179, 187, 1);
  }
}

// 第三级：分类下的排放源列表
.category-items {
  width: 100%;
  padding-left: 24px;
}

.emission-source-group {
  width: 100%;
  margin-bottom: 8px;
}

// 第三级：排放源名称样式
.emission-source-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding-left: 10px;
  margin-top: 4px;
}

.emission-source-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.emission-source-label {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  color: rgba(0, 43, 37, 1);

  // .emission-source-header:not(.emission-source-expanded) & {
  //   color: rgba(176, 192, 191, 1);
  // }
}

// 活动数据列表样式
.activity-data-list {
  width: 100%;
  padding-left: 32px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 8px 0;
}

.activity-data-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 14px;
  font-weight: 700;
  color: rgba(5, 32, 49, 1);
  line-height: 20px;
  gap: 20px;
}

.activity-type {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-value {
  flex: 1;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-year {
  flex: 2;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-location {
  flex: 2;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 在线填报按钮样式
.online-fill-button {
  position: absolute;
  // top: 20px;
  bottom: 12px;
  right: 12px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  img {
    width: 100px;
    height: 32px;
    object-fit: contain;
  }
}
</style>
